import * as q from "../../queries.ts";
import { Channel, Follow } from "../../structs.ts";
import { formatError, RpcClient } from "../api.ts";
import { authenticatedAppViewRequest, type AuthState, authState, signIn } from "../auth.ts";
import {
  accentColor,
  ownChannels,
  ownRecords,
  removeChannel,
  saveChannel,
  setRelation,
} from "../channels.ts";
import { config } from "../config.ts";
import { canCallRpc, canWriteCollection } from "../permissions.ts";
import { createTid, rkeyOf } from "../repo.ts";
import { dropdown } from "./menu.tsx";
import "vanilla-colorful/hex-color-picker.js";
import "vanilla-colorful/hex-input.js";
import { confirmDialog } from "./confirm.tsx";

/**
 * Dropdown that narrows a profile feed to one channel, plus the channel-management dialog for
 * signed-in viewers. Channels of other actors come from the profile; own channels are re-read from
 * the PDS so edits made in the dialog show immediately.
 */
export function ChannelMenu(
  actor: `did:${string}`,
  known: readonly q.ChannelView[],
  select: (channel?: string) => void,
  signal: AbortSignal,
): Element {
  const trigger = (
    <button class="profile-tab channel-menu-button" type="button" aria-haspopup="menu">
      Channels
    </button>
  ) as HTMLButtonElement;
  const menu = <div class="menu menu-right" role="menu"></div> as HTMLElement;
  const panel = <span class="menu-anchor channel-menu">{trigger}{menu}</span>;
  dropdown(trigger, menu, signal);
  let controller: AbortController | undefined;
  let dialog: HTMLDialogElement | undefined;
  let selected = "default";
  const subscription = authState.subscribeImmediate((state) => {
    controller?.abort();
    controller = new AbortController();
    void load(state, AbortSignal.any([signal, controller.signal]));
  });
  signal.addEventListener("abort", () => {
    subscription.unsubscribe();
    controller?.abort();
  }, { once: true });

  async function load(state: AuthState, signal: AbortSignal, openSettings = false): Promise<void> {
    if (signal.aborted) return;
    dialog?.remove();
    dialog = undefined;
    try {
      const viewer = state.status === "authenticated" ? state.did as `did:${string}` : undefined;
      const channels = viewer === actor ? await ownChannels(viewer, signal) : [...known];

      const canFollow = state.status === "authenticated" &&
        ["create", "delete"].every((action) =>
          canWriteCollection(state.scopes, Follow.id, action as "create" | "delete")
        );
      const canExclude = state.status === "authenticated" &&
        [q.getChannelExclusions.id, q.setChannelExcluded.id].every((method) =>
          canCallRpc(state.scopes, method, config.appViewService)
        );
      const canManage = state.status === "authenticated" &&
        ["create", "update", "delete"].every((action) =>
          canWriteCollection(state.scopes, Channel.id, action as "create" | "update" | "delete")
        );
      const explicit = new Set<string>();
      const excluded = new Set<string>();
      const privateClient = new RpcClient(authenticatedAppViewRequest);
      if (viewer) {
        for (const entry of await ownRecords(viewer, Follow.id, signal)) {
          if (entry.value.$type === Follow.id && entry.value.subject === actor) {
            explicit.add(entry.value.channel ?? "");
          }
        }
        if (canExclude) {
          const result = await privateClient.call(q.getChannelExclusions, {
            parameters: { actor },
            signal,
          });
          for (const entry of result.exclusions) excluded.add(entry.channel ?? "");
        }
      }
      if (signal.aborted) return;

      const views: Array<{
        key: string;
        label: string;
        channel?: string;
        accentColor?: string;
      }> = [
        { key: "default", label: "Default channels", channel: "default" },
        { key: "main", label: "Main", channel: "main" },
        ...channels.map((channel) => {
          const accent = accentColor(channel.record.accentColor);
          return {
            key: rkeyOf(channel.uri),
            label: channel.record.name,
            channel: rkeyOf(channel.uri),
            ...(accent && { accentColor: accent }),
          };
        }),
      ];
      if (!views.some((view) => view.key === selected)) {
        selected = "default";
        select("default");
      }
      menu.replaceChildren();
      const items: HTMLButtonElement[] = [];
      for (const view of views) {
        let dot: Element | undefined;
        if (view.accentColor) {
          const swatch = <span class="channel-dot" aria-hidden="true"></span> as HTMLElement;
          swatch.style.backgroundColor = view.accentColor;
          dot = swatch;
        }
        const item = (
          <button type="button" role="menuitemradio" aria-checked={String(view.key === selected)}>
            {dot}
            {view.label}
          </button>
        ) as HTMLButtonElement;
        item.addEventListener("click", () => {
          selected = view.key;
          for (const other of items) other.ariaChecked = "false";
          item.ariaChecked = "true";
          trigger.textContent = view.key === "default" ? "Channels" : view.label;
          trigger.classList.toggle("active", view.key !== "default");
          select(view.channel);
        });
        items.push(item);
        menu.append(item);
      }
      const current = views.find((view) => view.key === selected)!;
      trigger.textContent = current.key === "default" ? "Channels" : current.label;
      trigger.classList.toggle("active", current.key !== "default");
      if (!viewer) return;

      const status = <p class="status" role="status"></p>;
      const controls = <fieldset class="channel-controls"></fieldset> as HTMLFieldSetElement;
      const settings = <dialog class="channel-dialog"></dialog> as HTMLDialogElement;
      dialog = settings;
      const run = async (task: () => Promise<void>) => {
        controls.disabled = true;
        status.textContent = "Saving…";
        try {
          await task();
          await load(state, signal, true);
        } catch (error) {
          if (!signal.aborted) status.textContent = formatError(error);
        } finally {
          controls.disabled = false;
        }
      };
      const editor = (channel?: q.ChannelView): Element => {
        const key = channel ? rkeyOf(channel.uri) : createTid();
        const name = (
          <input
            aria-label="Channel name"
            value={channel?.record.name ?? ""}
            maxLength={640}
            required
          />
        ) as HTMLInputElement;
        const description = (
          <textarea aria-label="Channel description" maxLength={10240}>
            {channel?.record.description ?? ""}
          </textarea>
        ) as HTMLTextAreaElement;
        const included = (
          <input type="checkbox" checked={channel?.record.includeByDefault ?? false} />
        ) as HTMLInputElement;
        const initialAccent = accentColor(channel?.record.accentColor) ?? "";
        let accent = initialAccent;
        const picker = <hex-color-picker aria-label="Accent color picker" /> as unknown as
          & HTMLElement
          & { color: string };
        const field = <hex-input aria-label="Accent color hex value" /> as unknown as
          & HTMLElement
          & { color: string };
        const pick = <div class="channel-accent-pick"></div> as HTMLElement;
        const swatch = (
          <button type="button" class="color-swatch" aria-label="Pick accent color" />
        ) as HTMLButtonElement;
        const colorDialog = <dialog class="color-dialog"></dialog> as HTMLDialogElement;
        colorDialog.append(picker);
        colorDialog.addEventListener("click", (event) => {
          if (event.target === colorDialog) {
            colorDialog.close();
          }
        });
        swatch.addEventListener("click", () =>
          colorDialog.showModal());
        const useAccent = (
          <input type="checkbox" checked={initialAccent !== ""} />
        ) as HTMLInputElement;
        const paintPick = () =>
          pick.classList.toggle("disabled", !useAccent.checked);
        const setAccent = (value: string) => {
          accent = value;
          picker.color = value;
          field.color = value;
          swatch.style.backgroundColor = value;
          swatch.style.borderColor = value;
        };
        if (initialAccent) {
          setAccent(initialAccent);
        }
        const syncAccent = (event: Event) => {
          if (!useAccent.checked) {
            useAccent.checked = true;
            paintPick();
          }
          setAccent((event as CustomEvent<{ value: string }>).detail.value);
        };
        picker.addEventListener("color-changed", syncAccent);
        field.addEventListener("color-changed", syncAccent);
        pick.append(field, swatch, colorDialog);
        useAccent.addEventListener("change", paintPick);
        paintPick();
        const actions = <div class="channel-editor-actions"></div>;
        actions.append(<button type="submit">{channel ? "Save" : "Create"}</button>);
        if (channel) {
          actions.append(
            <button
              class="danger-action"
              type="button"
              _onclick={() => {
                void confirmDialog(
                  "Delete this channel? Its posts stay public and will not move to main.",
                  "Delete",
                ).then((confirmed) => {
                  if (confirmed) {
                    void run(() =>
                      removeChannel(viewer, channel, signal)
                    );
                  }
                });
              }}
            >
              Delete
            </button>,
          );
        }
        return (
          <form
            class="channel-editor"
            _onsubmit={(event) => {
              event.preventDefault();
              if (useAccent.checked && !accentColor(accent)) {
                status.textContent = "Accent colors look like #208bfe.";
                return;
              }
              void run(() =>
                saveChannel(
                  viewer,
                  key,
                  {
                    $type: Channel.id,
                    name: name.value.trim(),
                    description: description.value,
                    includeByDefault: included.checked,
                    ...(useAccent.checked && { accentColor: accent }),
                  },
                  channel?.cid,
                  signal,
                )
              );
            }}
          >
            <label>Name{name}</label>
            <label>Description{description}</label>
            <label class="check-control">{included} Include with account follows</label>
            <div class="channel-accent-row">
              <label class="check-control">{useAccent} Accent color</label>
              {pick}
            </div>
            {actions}
          </form>
        );
      };

      if (!canFollow || !canExclude || (viewer === actor && !canManage)) {
        controls.append(
          <button
            type="button"
            _onclick={() => void run(() => signIn(viewer, location.pathname))}
          >
            Authorize channel controls
          </button>,
        );
      }
      for (const channel of [undefined, ...channels]) {
        const key = channel && rkeyOf(channel.uri);
        const row = <section class="channel-row"></section>;
        const heading = <header class="channel-row-heading"></header>;
        const title = <span class="channel-title"></span>;
        const dotColor = accentColor(channel?.record.accentColor);
        if (dotColor) {
          const dot = <span class="channel-dot" aria-hidden="true"></span> as HTMLElement;
          dot.style.backgroundColor = dotColor;
          title.append(dot);
        }
        title.append(<strong>{channel?.record.name ?? "Main"}</strong>);
        heading.append(title);
        if (!channel || channel.record.includeByDefault) heading.append(<small>Default</small>);
        else heading.append(<small>Opt-in</small>);
        row.append(heading);
        if (channel?.record.description) row.append(<p>{channel.record.description}</p>);

        if (viewer !== actor) {
          const actions = <div class="channel-actions"></div>;
          actions.append(
            <button
              type="button"
              disabled={!canFollow}
              _onclick={() =>
                void run(() =>
                  setRelation(viewer, Follow.id, actor, !explicit.has(key ?? ""), key, signal)
                )}
            >
              {explicit.has(key ?? "")
                ? (key ? "Unfollow channel" : "Unfollow")
                : (key ? "Follow channel" : "Follow")}
            </button>,
          );
          if (canExclude) {
            const checkbox = (
              <input type="checkbox" checked={excluded.has(key ?? "")} />
            ) as HTMLInputElement;
            checkbox.addEventListener("change", () =>
              void run(() =>
                privateClient.call(q.setChannelExcluded, {
                  parameters: {},
                  input: { actor, ...(key && { channel: key }), excluded: checkbox.checked },
                  signal,
                })
              ));
            actions.append(<label class="check-control">{checkbox} Exclude</label>);
          }
          row.append(actions);
          if (key && explicit.has("") && channel?.record.includeByDefault && !explicit.has(key)) {
            row.append(<small>Included through your account follow</small>);
          }
        } else if (canManage && channel) {
          row.append(
            <details class="channel-editor-disclosure">
              <summary>Edit</summary>
              {editor(channel)}
            </details>,
          );
        }
        controls.append(row);
      }
      if (viewer === actor && canManage) {
        controls.append(
          <details class="channel-editor-disclosure new-channel">
            <summary>New channel</summary>
            {editor()}
          </details>,
        );
      }

      const close = (
        <button class="channel-dialog-close" type="button" aria-label="Close">×</button>
      );
      close.addEventListener("click", () => settings.close());
      settings.append(
        <section class="channel-dialog-content">
          <header>
            <h2>Channels</h2>
            {close}
          </header>
          {controls}
          {status}
        </section>,
      );
      const open = (
        <button type="button" role="menuitem" class="menu-separated">Manage channels…</button>
      );
      open.addEventListener("click", () => settings.showModal());
      menu.append(open);
      panel.append(settings);
      if (openSettings) settings.showModal();
    } catch (error) {
      if (!signal.aborted) {
        const retry = <button type="button" role="menuitem">Retry loading channels</button>;
        retry.addEventListener("click", () => void load(state, signal));
        menu.replaceChildren(<p class="status">{formatError(error)}</p>, retry);
      }
    }
  }
  return panel;
}
